import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, TrendingUp, Info, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { toast } from 'sonner';
import { CoverImage } from './CoverImage';
import { useRecommendationFeedback } from '@/hooks/useRecommendationFeedback';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { QuickReviewDialog } from './QuickReviewDialog';
import { ReadingFormat } from '@/lib/readingFormatUtils';

interface BookRecommendationCardProps {
  title: string;
  author: string;
  reason?: string;
  bookId?: string;
  isbn13?: string;
  isbn10?: string;
  coverUrl?: string;
  confidenceScore?: number; // 0-100
  matchDetails?: string; // Specific examples from user's history
  sourceBooks?: Array<{
    id: string;
    title: string;
    reason: string;
  }>;
  recommendationType?: 'safe_bet' | 'stretch' | 'wildcard' | 'classic';
  onDismiss?: () => void;
  compact?: boolean; // Smaller card for chat interface
}

export function BookRecommendationCard({ 
  title, 
  author,
  reason,
  bookId,
  isbn13,
  isbn10,
  coverUrl,
  confidenceScore,
  matchDetails,
  sourceBooks,
  recommendationType,
  onDismiss,
  compact = false
}: BookRecommendationCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toggleSave, loading: isSaving, isSaved } = useSaveToTBR(
    bookId || '', 
    undefined, 
    { title, author, isbn13, isbn10 }
  );
  const { submitFeedback, loading: feedbackLoading } = useRecommendationFeedback();
  const [dismissed, setDismissed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewBookId, setReviewBookId] = useState<string | undefined>(bookId);
  const isMobile = useIsMobile();
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [libraryShelf, setLibraryShelf] = useState<string | null>(null);

  // Check if book is already in user's library by ISBN or bookId
  useEffect(() => {
    const checkLibraryStatus = async () => {
      if (!user) return;
      
      try {
        // Build OR query for bookId and ISBNs
        let query = supabase
          .from('user_books')
          .select('shelf, book_id, books!inner(isbn13, isbn10)')
          .eq('user_id', user.id);
        
        if (bookId) {
          const { data } = await query.eq('book_id', bookId).maybeSingle();
          if (data) {
            setIsInLibrary(true);
            setLibraryShelf(data.shelf);
            return;
          }
        }
        
        // Check by ISBN if no bookId match
        if (isbn13 || isbn10) {
          const { data: allBooks } = await supabase
            .from('user_books')
            .select('shelf, books!inner(isbn13, isbn10)')
            .eq('user_id', user.id);
          
          if (allBooks) {
            const match = allBooks.find((ub: any) => 
              (isbn13 && ub.books?.isbn13 === isbn13) || 
              (isbn10 && ub.books?.isbn10 === isbn10)
            );
            if (match) {
              setIsInLibrary(true);
              setLibraryShelf(match.shelf);
            }
          }
        }
      } catch (err) {
        console.error('Error checking library status:', err);
      }
    };
    
    checkLibraryStatus();
  }, [user, bookId, isbn13, isbn10]);

  // Combine the hook's isSaved with our library check
  const bookIsSaved = isSaved || (isInLibrary && libraryShelf === 'to-read');

  const handleAddToTBR = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    await toggleSave();
    
    // Log upvote feedback when adding to TBR
    await submitFeedback({
      bookId,
      bookTitle: title,
      bookAuthor: author,
      isbn13,
      isbn10,
      feedbackType: 'upvote',
      recommendationReason: reason,
    });
  };

  const handleViewDetails = async () => {
    try {
      if (bookId) {
        navigate(`/book/${bookId}`);
        return;
      }

      // Try to find existing book by ISBNs
      if (isbn13 || isbn10) {
        const orFilters = [
          isbn13 ? `isbn13.eq.${isbn13}` : null,
          isbn10 ? `isbn10.eq.${isbn10}` : null,
        ].filter(Boolean).join(',');

        const { data: existing, error: findError } = await supabase
          .from('books')
          .select('id')
          .or(orFilters)
          .limit(1)
          .maybeSingle();

        if (findError) throw findError;
        if (existing?.id) {
          navigate(`/book/${existing.id}`);
          return;
        }
      }

      // Create minimal book record then navigate
      const { data: created, error: insertError } = await supabase
        .from('books')
        .insert({
          title,
          authors: author ? [author] : [],
          isbn13: isbn13 || null,
          isbn10: isbn10 || null,
          cover_url: coverUrl || null,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      navigate(`/book/${created.id}`);
    } catch (err) {
      console.error('Error opening book from rec card', err);
      toast.error('Unable to open book', {
        description: 'Please try again in a moment.',
      });
    }
  };

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Submit downvote feedback and dismiss
    const result = await submitFeedback({
      bookId,
      bookTitle: title,
      bookAuthor: author,
      isbn13,
      isbn10,
      feedbackType: 'downvote',
      recommendationReason: reason,
    });

    if (result.success) {
      setDismissed(true);
      onDismiss?.();
    }
  };

  if (dismissed) {
    return null;
  }

  const handleAlreadyRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      // If we already have a bookId, use it
      if (bookId) {
        setReviewBookId(bookId);
        setReviewDialogOpen(true);
        return;
      }

      // Try to find existing book by ISBNs
      if (isbn13 || isbn10) {
        const orFilters = [
          isbn13 ? `isbn13.eq.${isbn13}` : null,
          isbn10 ? `isbn10.eq.${isbn10}` : null,
        ].filter(Boolean).join(',');

        const { data: existing, error: findError } = await supabase
          .from('books')
          .select('id')
          .or(orFilters)
          .limit(1)
          .maybeSingle();

        if (findError) throw findError;
        if (existing?.id) {
          setReviewBookId(existing.id);
          setReviewDialogOpen(true);
          return;
        }
      }

      // Create minimal book record
      const { data: created, error: insertError } = await supabase
        .from('books')
        .insert({
          title,
          authors: author ? [author] : [],
          isbn13: isbn13 || null,
          isbn10: isbn10 || null,
          cover_url: coverUrl || null,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      setReviewBookId(created.id);
      setReviewDialogOpen(true);
    } catch (err) {
      console.error('Error preparing book for review', err);
      toast.error('Unable to open review dialog', {
        description: 'Please try again in a moment.',
      });
    }
  };

  const handleReviewSubmit = async (data: {
    rating: number;
    reviewText: string;
    finishedAt?: Date;
    startedAt?: Date;
    privacy: 'public' | 'private';
    tags?: string[];
    reading_format?: ReadingFormat;
  }) => {
    if (!reviewBookId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create/update user_books entry
      // Update existing or insert new user_books record without relying on onConflict
      const updateData: any = {
        shelf: 'read',
        rating_star: data.rating,
        review_text: data.reviewText,
        privacy: data.privacy,
        tags: data.tags || [],
        reading_format: data.reading_format,
        finished_at: data.finishedAt
          ? data.finishedAt.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      };

      if (data.startedAt) {
        updateData.started_at = data.startedAt.toISOString().split('T')[0];
      }

      const { data: updatedRows, error: updateError } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('user_id', user.id)
        .eq('book_id', reviewBookId)
        .select('id');

      if (updateError) throw updateError;

      if (!updatedRows || updatedRows.length === 0) {
        const { error: insertError } = await supabase
          .from('user_books')
          .insert({
            user_id: user.id,
            book_id: reviewBookId,
            ...updateData,
          });
        if (insertError) throw insertError;
      }

      // Log positive feedback
      await submitFeedback({
        bookId: reviewBookId,
        bookTitle: title,
        bookAuthor: author,
        isbn13,
        isbn10,
        feedbackType: 'upvote',
        recommendationReason: reason,
      });

      toast.success('Review saved!');
      setReviewDialogOpen(false);
      setDismissed(true);
    } catch (err) {
      console.error('Error saving review:', err);
      toast.error('Failed to save review', {
        description: 'Please try again.',
      });
    }
  };

  // Shared details content for both hover card and sheet
  const DetailsContent = () => (
    <div className="space-y-3 max-w-full pr-2">
      {sourceBooks && sourceBooks.length > 0 && (
        <div className="flex gap-2">
          <BookOpen className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1 max-w-full">
            <p className="text-xs font-medium">Because you read</p>
            {sourceBooks.slice(0, 2).map((source, idx) => (
              <div key={idx} className="text-xs text-muted-foreground leading-relaxed break-words max-w-full" style={{ textWrap: 'pretty' }}>
                <span className="font-medium">{source.title}</span>
                {source.reason && <span> — {source.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {reason && (
        <div className="flex gap-2 pt-2 border-t">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5 max-w-full">
            <p className="text-xs font-medium">Why we recommend this</p>
            <p className="text-xs text-muted-foreground leading-relaxed break-words max-w-full whitespace-pre-wrap" style={{ textWrap: 'pretty' }}>
              {reason}
            </p>
          </div>
        </div>
      )}
      
      {matchDetails && (
        <div className="flex gap-2 pt-2 border-t">
          <TrendingUp className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5 max-w-full">
            <p className="text-xs font-medium">Based on your history</p>
            <p className="text-xs text-muted-foreground leading-relaxed break-words max-w-full whitespace-pre-wrap" style={{ textWrap: 'pretty' }}>
              {matchDetails}
            </p>
          </div>
        </div>
      )}
      
      {confidenceScore !== undefined && (
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Match confidence</span>
            <span className="font-medium">{confidenceScore}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all"
              style={{ width: `${confidenceScore}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const hasDetails = reason || matchDetails || sourceBooks;

  // Shared card content
  const CardContent = () => (
    <>
      <div className={cn(
        "relative overflow-hidden rounded-lg bg-muted",
        compact ? "aspect-[2/3] w-16 flex-shrink-0" : "aspect-[2/3] w-full mb-3"
      )}>
        {coverUrl || bookId || isbn13 || isbn10 ? (
          <CoverImage
            bookId={bookId}
            isbn13={isbn13}
            isbn10={isbn10}
            title={title}
            author={author}
            coverUrl={coverUrl}
            size={compact ? "xs" : "sm"}
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className={cn("text-muted-foreground", compact ? "w-5 h-5" : "w-8 h-8")} />
          </div>
        )}
        
        {!compact && confidenceScore !== undefined && (
          <Badge 
            variant={confidenceScore >= 80 ? "default" : confidenceScore >= 60 ? "secondary" : "outline"}
            className="absolute top-2 right-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {confidenceScore}% match
          </Badge>
        )}
        
        {!compact && recommendationType === 'wildcard' && (
          <Badge 
            variant="outline" 
            className="absolute bottom-2 left-2 text-xs bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-700"
          >
            🎲 Wild Card
          </Badge>
        )}
      </div>
      
      <div className={cn("flex flex-col min-w-0", compact ? "flex-1" : "flex-1")}>
        <h3 className={cn(
          "font-medium group-hover:text-primary transition-colors leading-tight",
          compact ? "text-xs line-clamp-2" : "text-sm line-clamp-2 mb-1 h-10"
        )}>
          {title}
        </h3>
        
        <p className={cn(
          "text-muted-foreground line-clamp-1",
          compact ? "text-xs" : "text-xs mb-3"
        )}>
          {author}
        </p>

        {compact && reason && (
          <p className="text-xs text-muted-foreground line-clamp-3 mt-1 leading-relaxed">
            {reason}
          </p>
        )}

        {!compact && (
          <div className="mt-auto space-y-2">
            {isInLibrary && libraryShelf === 'read' ? (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="w-full text-xs"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Already Read
              </Button>
            ) : isInLibrary && libraryShelf === 'reading' ? (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="w-full text-xs"
              >
                <BookOpen className="w-3 h-3 mr-1" />
                Currently Reading
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant={bookIsSaved ? "outline" : "default"}
                  onClick={handleAddToTBR}
                  disabled={isSaving || bookIsSaved}
                  className="w-full text-xs"
                >
                  {bookIsSaved ? 'In TBR' : 'Add to TBR'}
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAlreadyRead}
                  className="w-full text-xs"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Already Read
                </Button>
              </>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              disabled={feedbackLoading}
              className="w-full text-xs text-muted-foreground hover:text-foreground"
            >
              Not interested
            </Button>
          </div>
        )}

        {compact && (
          <div className="flex justify-end mt-auto pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              disabled={feedbackLoading}
              className="h-7 px-2 text-xs text-muted-foreground"
            >
              ✕
            </Button>
          </div>
        )}
      </div>
    </>
  );

  // For compact mode, use a simple horizontal layout
  if (compact) {
    return (
      <div className="flex items-start gap-3 w-full" onClick={handleViewDetails}>
        <CardContent />
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Sheet with info button */}
      {isMobile && hasDetails ? (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <div className="relative h-full">
            <div className="group cursor-pointer flex flex-col h-full" onClick={handleViewDetails}>
              <CardContent />
            </div>
            <SheetTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 left-2 h-6 w-6"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-4 w-4" />
              </Button>
            </SheetTrigger>
          </div>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto px-4 pb-safe-bottom">
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <DetailsContent />
            </div>
          </SheetContent>
        </Sheet>
      ) : !isMobile && hasDetails ? (
        /* Desktop: HoverCard */
        <HoverCard openDelay={200}>
          <HoverCardTrigger asChild>
            <div className="group cursor-pointer flex flex-col h-full" onClick={handleViewDetails}>
              <CardContent />
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-[480px] max-w-[calc(100vw-2rem)]" side="top">
            <DetailsContent />
          </HoverCardContent>
        </HoverCard>
      ) : (
        /* No details - just the card */
        <div className="group cursor-pointer flex flex-col h-full" onClick={handleViewDetails}>
          <CardContent />
        </div>
      )}

      {/* Review Dialog */}
      {reviewBookId && (
        <QuickReviewDialog
          isOpen={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          book={{
            id: reviewBookId,
            title,
            author,
          }}
          onSubmit={handleReviewSubmit}
        />
      )}
    </>
  );
}
